import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getVineyards } from '../lib/api';
import { StarRating } from '../components/StarRating';
import { MapPin, AlertCircle } from 'lucide-react';

// Fix default icon issue with Webpack/CRA — use custom gold pin
const goldIcon = L.divIcon({
  className: 'custom-pin',
  html: `
    <div style="
      width: 28px; height: 28px; border-radius: 50% 50% 50% 0;
      background: #d4af37; transform: rotate(-45deg);
      border: 2px solid #1c1917;
      box-shadow: 0 4px 10px rgba(0,0,0,0.6);
      display:flex; align-items:center; justify-content:center;
    ">
      <div style="
        width: 10px; height: 10px; border-radius: 50%;
        background: #1c1917; transform: rotate(45deg);
      "></div>
    </div>
  `,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const MapView = () => {
  const [vineyards, setVineyards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getVineyards({ limit: 500 });
        setVineyards(data);
      } catch (e) {
        console.error('Failed to load vineyards:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const mapped = useMemo(
    () => vineyards.filter(v => typeof v.latitude === 'number' && typeof v.longitude === 'number'),
    [vineyards]
  );
  const missing = vineyards.length - mapped.length;

  const center = useMemo(() => {
    if (mapped.length === 0) return [43.5, 2.5]; // Europe fallback
    const lat = mapped.reduce((s, v) => s + v.latitude, 0) / mapped.length;
    const lng = mapped.reduce((s, v) => s + v.longitude, 0) / mapped.length;
    return [lat, lng];
  }, [mapped]);

  return (
    <div className="min-h-screen pt-8 pb-20" data-testid="map-view-page">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Discover</p>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-50">Vineyard Map</h1>
          <p className="text-stone-500 mt-3 text-sm">
            Explore vineyards around the world. {mapped.length} located
            {missing > 0 && (
              <span className="text-stone-600"> &middot; {missing} pending coordinates</span>
            )}
          </p>
        </div>

        {loading ? (
          <div className="h-[70vh] bg-stone-900/40 border border-white/5 animate-pulse" />
        ) : (
          <div
            className="h-[70vh] w-full border border-white/10 overflow-hidden"
            data-testid="vineyard-map"
          >
            <MapContainer
              center={center}
              zoom={mapped.length === 1 ? 10 : 4}
              scrollWheelZoom
              style={{ height: '100%', width: '100%', background: '#0c0a09' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={(cluster) => L.divIcon({
                  html: `
                    <div style="
                      width: 40px; height: 40px; border-radius: 50%;
                      background: rgba(212,175,55,0.9);
                      color: #1c1917;
                      display: flex; align-items: center; justify-content: center;
                      border: 2px solid #1c1917;
                      font-family: 'Playfair Display', serif;
                      font-weight: 700;
                      font-size: 14px;
                      box-shadow: 0 4px 12px rgba(0,0,0,0.6);
                    ">${cluster.getChildCount()}</div>
                  `,
                  className: 'cluster-pin',
                  iconSize: [40, 40],
                })}
              >
                {mapped.map((v) => (
                  <Marker
                    key={v.id}
                    position={[v.latitude, v.longitude]}
                    icon={goldIcon}
                  >
                    <Popup className="vineyard-popup">
                      <div className="min-w-[220px]">
                        {v.photos?.[0] && (
                          <img
                            src={v.photos[0]}
                            alt={v.name}
                            className="w-full h-28 object-cover mb-2"
                          />
                        )}
                        <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-1">
                          {v.region}, {v.country}
                        </div>
                        <div className="font-serif text-stone-50 text-lg leading-tight mb-1">
                          {v.name}
                        </div>
                        <div className="text-xs text-stone-400 mb-2">{v.producer}</div>
                        <div className="flex items-center gap-2 mb-2">
                          <StarRating rating={v.average_rating || 0} readonly size="sm" />
                          <span className="text-xs text-stone-500">
                            {v.rating_count || 0}
                          </span>
                        </div>
                        <Link
                          to={`/vineyards/${v.id}`}
                          className="inline-block text-xs uppercase tracking-widest text-[#d4af37] hover:text-stone-50 transition-colors"
                          data-testid={`map-popup-link-${v.id}`}
                        >
                          View Details &rarr;
                        </Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        )}

        {!loading && missing > 0 && (
          <div className="mt-8 p-6 bg-stone-900/30 border border-white/5 flex items-start gap-4" data-testid="missing-coords-notice">
            <AlertCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-stone-300 text-sm">
                {missing} vineyard{missing === 1 ? '' : 's'} {missing === 1 ? 'is' : 'are'} missing map coordinates.
                Edit any vineyard to add latitude &amp; longitude to place it on the map.
              </p>
              <Link
                to="/vineyards"
                className="inline-flex items-center gap-1 mt-2 text-xs uppercase tracking-widest text-[#d4af37] hover:text-stone-50 transition-colors"
              >
                <MapPin className="w-3 h-3" /> Go to Vineyards
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;
